<script setup lang="ts">
import { ref, computed, type ComputedRef, type PropType } from 'vue'
import { storeToRefs } from 'pinia'
import { useAuth } from '@/stores/auth'
import { usePlayerSettings } from '@/stores/playerSettings'
import { useFlags } from '@/composables/useFeatureFlags'
import NavigationPaneComponent, { type Profile } from './NavigationPaneComponent.vue'
import type { Position, NavItem } from './types/NavigationPane'
import { usePlayerStats } from '@/composables/usePlayerStats'
import { detectDeviceType } from '@/lib/utils'

const props = defineProps({
  customNavigationItems: {
    type: Array as PropType<NavItem[]>,
    default: () => []
  },
  customUserProfile: {
    type: Object as PropType<Profile>,
    default: () => ({})
  },
  customConfig: {
    type: Object,
    default: () => ({})
  },
  selectedItemId: {
    type: String,
    default: undefined
  }
})

const { isLoggedIn } = useAuth()
const { nickname } = storeToRefs(usePlayerSettings())
const { rankData } = usePlayerStats()

const featureFlags = useFlags(['aip-559_show-study-mode-on-homepage'])
const showStudyMode = featureFlags.isEnabled('aip-559_show-study-mode-on-homepage')

const defaultNavigationItems = computed(() => [
  ...(showStudyMode.value
    ? [
        {
          id: 'study',
          label: 'Study',
          href: '/study-mode'
        }
      ]
    : []),
  {
    id: 'practice',
    label: 'Practice ',
    href: '/games',
    disabled: !isLoggedIn
  },
  {
    id: 'compete',
    label: 'Compete',
    href: '/compete',
    showDivider: true,
    disabled: !isLoggedIn
  },
  {
    id: 'analysis',
    label: 'Analysis',
    href: '/analysis-mode2',
    disabled: !isLoggedIn
  }
])

const defaultUserProfile = computed(() => ({
  name: nickname.value || 'MaxMoneymaker',
  tag: rankData.value.rankTitle,
  image: null,
  rank: rankData.value.rank
}))

const isMobile = ref(detectDeviceType() === 'mobile')

const defaultNavigationConfig = computed(() => ({
  position: (isMobile.value ? 'bottom' : 'left') as Position,
  canExpand: true,
  showSettings: false,
  showAuth: true,
  showCoachAI: false,
  isLoggedIn: !!isLoggedIn
}))

const navigationItems: ComputedRef<NavItem[]> = computed(() =>
  props.customNavigationItems.length ? props.customNavigationItems : defaultNavigationItems.value
)
const userProfile: ComputedRef<Profile> = computed(() =>
  Object.keys(props.customUserProfile).length ? props.customUserProfile : defaultUserProfile.value
)
const navigationConfig = computed(() =>
  Object.keys(props.customConfig).length ? props.customConfig : defaultNavigationConfig.value
)
</script>

<template>
  <div class="z-10 h-10 md:h-auto">
    <NavigationPaneComponent
      :items="navigationItems"
      :profile="userProfile"
      :config="navigationConfig"
      :selectedItemId="selectedItemId"
    />
  </div>
</template>
